import { useEffect, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { UserProfile, WorkOsWidgets } from '@workos-inc/widgets'
import { Theme } from '@radix-ui/themes'
import { ArrowLeft } from 'lucide-react'

import { EngineerLayout } from '../../components/layout/EngineerLayout'
import { useMe } from '../../auth/hooks/useMe'
import { useWidgetToken } from '../../auth/hooks/useWidgetToken'
import { lightenColor, darkenColor } from '../../utils/color'
import { PhoneProfileRow } from '../../components/profile/PhoneProfileRow'
import { NotificationPreferences } from '../../components/profile/NotificationPreferences'

function buildAccentOverrides(hex: string): React.CSSProperties {
  return {
    '--accent-1': lightenColor(hex, 95),
    '--accent-2': lightenColor(hex, 90),
    '--accent-3': lightenColor(hex, 80),
    '--accent-4': lightenColor(hex, 70),
    '--accent-5': lightenColor(hex, 60),
    '--accent-6': lightenColor(hex, 45),
    '--accent-7': lightenColor(hex, 30),
    '--accent-8': lightenColor(hex, 15),
    '--accent-9': hex,
    '--accent-10': darkenColor(hex, 8),
    '--accent-11': darkenColor(hex, 20),
    '--accent-12': darkenColor(hex, 35),
    '--accent-a9': hex,
    '--accent-a10': darkenColor(hex, 8),
    '--accent-contrast': '#fff',
    '--woswidgets-accent-color': hex,
  } as React.CSSProperties
}

export function EngineerProfilePage() {
  const getProfileToken = useWidgetToken([])
  const navigate = useNavigate()
  const { tenantSlug } = useParams()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()

  const returnTo = searchParams.get('returnTo')

  const { data: meData } = useMe()

  const brandColor = meData?.tenant?.primary_color || '#3b82f6'
  const accentOverrides = useMemo(() => buildAccentOverrides(brandColor), [brandColor])

  useEffect(() => {
    return () => {
      queryClient.invalidateQueries({ queryKey: ['auth-me'] })
    }
  }, [queryClient])

  return (
    <EngineerLayout hideHeader>
      <div className="px-4 pt-4 pb-2">
        <button
          onClick={() => navigate(returnTo === 'cart' ? `/${tenantSlug}/engineer/cart` : `/${tenantSlug}/engineer/more`)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 min-h-[44px]"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
      </div>

      <div className="px-4 pb-4">
        <h1 className="text-xl font-semibold text-gray-900">Profile</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your avatar, display name, and connected accounts.</p>
      </div>

      <div className="px-4 pb-6">
        {/* Wrap widget + phone row in a shared Radix <Theme> so both
            pieces resolve the same Radix Themes tokens (font, spacing,
            data-list label width). The engineer layout doesn't have a
            root Theme the way the admin side does. */}
        <Theme
          accentColor="blue"
          radius="medium"
          scaling="100%"
          style={accentOverrides}
          hasBackground={false}
        >
          <div className="engineer-profile-card">
            {/* WorkOS profile widget (avatar, name, email, password, etc.) */}
            <WorkOsWidgets>
              <UserProfile authToken={getProfileToken} />
            </WorkOsWidgets>

            {/* Phone row — styled to match WorkOS UserProfile table rows */}
            <PhoneProfileRow />
          </div>

          {/* Notifications — personal email preferences. Sits outside the
              profile card as its own section, and renders nothing at all for
              users with no reports. */}
          <div className="mt-4">
            <NotificationPreferences />
          </div>
        </Theme>
      </div>
      <style>{`
        .engineer-profile-card {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
        }
        /* Force the WorkOS widget's own wrapper to height auto so it
           doesn't expand and push the phone row off screen. */
        .engineer-profile-card > [class*="wosui"],
        .engineer-profile-card > [class*="widget"],
        .engineer-profile-card > [data-radix-root],
        .engineer-profile-card > div:first-child,
        .engineer-profile-card > div:first-child > * {
          height: auto !important;
          min-height: 0 !important;
          max-height: none !important;
        }
      `}</style>
    </EngineerLayout>
  )
}
